<script setup>
import {
  computed,
  defineAsyncComponent,
  nextTick,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { OnClickOutside } from '@vueuse/components';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MessageTemplatesAPI from 'dashboard/api/messageTemplates';
import { useAlert } from 'dashboard/composables';
import { sanitizeTemplateName } from 'dashboard/helper/templateNameSanitizer';
import { markdownToWhatsapp } from 'dashboard/helper/whatsappTemplateFormatter';
import {
  validateHeaderMediaFile,
  headerMediaMaxSizeLabel,
  headerMediaFormatsLabel,
  HEADER_MEDIA_LIMITS,
} from 'dashboard/helper/templateMediaLimits';

import Input from 'dashboard/components-next/input/Input.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const props = defineProps({
  inboxId: { type: [String, Number], required: true },
});

const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

const { t } = useI18n();

const CATEGORY_OPTIONS = ['MARKETING', 'UTILITY', 'AUTHENTICATION'].map(
  value => ({
    value,
    label: t(`INBOX_MGMT.MESSAGE_TEMPLATES.CATEGORY_OPTIONS.${value}`),
  })
);
const LANGUAGE_OPTIONS = ['pt_BR', 'en', 'en_US', 'es'].map(value => ({
  value,
  label: value,
}));
const HEADER_TYPE_OPTIONS = ['none', 'text', 'image', 'video', 'document'].map(
  value => ({
    value,
    label: t(
      `INBOX_MGMT.MESSAGE_TEMPLATES.FORM.HEADER_TYPE_OPTIONS.${value.toUpperCase()}`
    ),
  })
);
const BUTTON_TYPE_OPTIONS = ['QUICK_REPLY', 'URL'].map(value => ({
  value,
  label: t(`INBOX_MGMT.MESSAGE_TEMPLATES.FORM.BUTTON_TYPE_OPTIONS.${value}`),
}));

const form = reactive({
  name: '',
  category: 'MARKETING',
  language: 'pt_BR',
  headerType: 'none',
  headerText: '',
  footer: '',
  body: '',
  buttons: [],
});

const headerTextInputRef = ref(null);
const headerMediaHandle = ref('');
const headerMediaFileName = ref('');
const isUploadingMedia = ref(false);
const mediaUploadError = ref('');

// Emoji picker for the body only — Meta's guidelines don't allow emoji in the
// header or footer of a message template, only in the body. Shown in its own row
// below the field, same placement used in the conversation reply box. Picked
// emojis are appended to the end of the field; no cursor-position insertion,
// keeping this independent from the shared Editor component.
const useEmojiField = fieldKey => {
  const showPicker = reactive({ open: false });
  return {
    showPicker,
    toggle: () => {
      showPicker.open = !showPicker.open;
    },
    hide: () => {
      showPicker.open = false;
    },
    onSelect: emoji => {
      form[fieldKey] += emoji;
      showPicker.open = false;
    },
  };
};

const bodyEmoji = useEmojiField('body');

const sanitizedNamePreview = computed(() => sanitizeTemplateName(form.name));

const validationRules = {
  name: { required },
  body: { required },
};

const v$ = useVuelidate(validationRules, form);

// Meta's AUTHENTICATION category only supports a body with the verification code — no
// header, footer, or the button types this form offers (it needs a dedicated OTP
// button, which isn't implemented here). Switching into it drops whatever was set in
// those sections so a half-built, guaranteed-to-be-rejected template can't be saved.
const isAuthentication = computed(() => form.category === 'AUTHENTICATION');

const isHeaderMedia = computed(() =>
  ['image', 'video', 'document'].includes(form.headerType)
);

const headerMediaAccept = computed(
  () => HEADER_MEDIA_LIMITS[form.headerType]?.accept || ''
);

const headerMediaHint = computed(() =>
  t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.MEDIA_HINT', {
    formats: headerMediaFormatsLabel(form.headerType),
    maxSize: headerMediaMaxSizeLabel(form.headerType),
  })
);

const previewData = computed(() => ({
  headerText: form.headerType === 'text' ? form.headerText : '',
  headerMediaType: isHeaderMedia.value ? form.headerType.toUpperCase() : '',
  bodyText: markdownToWhatsapp(form.body),
  footerText: form.footer,
  buttons: form.buttons
    .filter(button => button.text)
    .map(button => button.text),
}));

const addButton = () => {
  if (form.buttons.length >= 3) return;
  form.buttons.push({ type: 'QUICK_REPLY', text: '', url: '' });
};

const removeButton = index => {
  form.buttons.splice(index, 1);
};

const sanitizeVariableName = name =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_');

// Named variables ({{var_1}}) for a single text field — sent alongside the template
// so the backend can convert them to positional syntax ({{1}}, {{2}}) and build the
// `example` the Meta API requires. Kept as an ordered list (not keyed by name) so
// renaming a variable can be done in place without losing its position/example.
// `maxCount` enforces Meta's limit of exactly one variable in the header. The next
// name reuses the smallest free var_N (not a running counter), so deleting a
// variable frees up its number for the next one added.
const useVariableField = (
  fieldKey,
  { maxCount = Infinity, inputRef = null } = {}
) => {
  const list = reactive([]);

  const canAdd = computed(() => list.length < maxCount);

  // The body uses the rich text Editor, which only exposes a cursor-aware insert via
  // this bus event; plain Input fields (header text) are spliced directly using the
  // native input's current selection, falling back to appending when it isn't focused.
  const insertPlaceholder = placeholder => {
    if (fieldKey === 'body') {
      emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, placeholder);
      return;
    }

    const el = inputRef?.value?.inputRef;
    const current = form[fieldKey];
    if (!el || document.activeElement !== el) {
      form[fieldKey] = `${current}${placeholder}`;
      return;
    }

    const { selectionStart, selectionEnd } = el;
    form[fieldKey] =
      current.slice(0, selectionStart) +
      placeholder +
      current.slice(selectionEnd);
    nextTick(() => {
      const cursor = selectionStart + placeholder.length;
      el.setSelectionRange(cursor, cursor);
      el.focus();
    });
  };

  // Smallest unused var_N rather than list.length + 1, so deleting a variable from
  // the middle of the list doesn't hand out a number already in use by another one.
  const nextVariableName = () => {
    const used = new Set(
      list
        .map(variable => Number(variable.name.match(/^var_(\d+)$/)?.[1]))
        .filter(n => !Number.isNaN(n))
    );
    let n = 1;
    while (used.has(n)) n += 1;
    return `var_${n}`;
  };

  const add = () => {
    if (!canAdd.value) return;
    const name = nextVariableName();
    list.push({ name, example: '' });
    insertPlaceholder(`{{${name}}}`);
  };

  // Captures the name before an edit so blur can swap every {{oldName}} occurrence
  // in the field for {{newName}} — renaming only in the list wouldn't update the text.
  const beginRename = variable => {
    variable.editingFrom = variable.name;
  };

  const commitRename = variable => {
    const sanitized =
      sanitizeVariableName(variable.name) || variable.editingFrom;
    if (sanitized !== variable.editingFrom) {
      form[fieldKey] = form[fieldKey]
        .split(`{{${variable.editingFrom}}}`)
        .join(`{{${sanitized}}}`);
    }
    variable.name = sanitized;
    delete variable.editingFrom;
  };

  const remove = index => {
    const [removed] = list.splice(index, 1);
    form[fieldKey] = form[fieldKey].split(`{{${removed.name}}}`).join('');
  };

  const reset = () => {
    list.splice(0, list.length);
  };

  const toExamples = () =>
    list.reduce((examples, variable) => {
      examples[variable.name] = variable.example;
      return examples;
    }, {});

  return {
    list,
    canAdd,
    add,
    beginRename,
    commitRename,
    remove,
    reset,
    toExamples,
  };
};

const bodyVariables = useVariableField('body');
const headerVariables = useVariableField('headerText', {
  maxCount: 1,
  inputRef: headerTextInputRef,
});

watch(
  () => form.category,
  newCategory => {
    if (newCategory !== 'AUTHENTICATION') return;
    form.headerType = 'none';
    form.headerText = '';
    form.footer = '';
    form.buttons = [];
    headerMediaHandle.value = '';
    headerMediaFileName.value = '';
    headerVariables.reset();
  }
);

const headerTypeLabel = () =>
  t(
    `INBOX_MGMT.MESSAGE_TEMPLATES.FORM.HEADER_TYPE_OPTIONS.${form.headerType.toUpperCase()}`
  );

const handleMediaChange = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  const validationErrorKey = validateHeaderMediaFile(file, form.headerType);
  if (validationErrorKey) {
    mediaUploadError.value = t(
      `INBOX_MGMT.MESSAGE_TEMPLATES.FORM.${validationErrorKey}`,
      {
        headerType: headerTypeLabel(),
        formats: headerMediaFormatsLabel(form.headerType),
        maxSize: headerMediaMaxSizeLabel(form.headerType),
      }
    );
    useAlert(mediaUploadError.value);
    event.target.value = '';
    return;
  }

  isUploadingMedia.value = true;
  mediaUploadError.value = '';
  try {
    const { data } = await MessageTemplatesAPI.uploadMedia(
      props.inboxId,
      file,
      form.headerType
    );
    headerMediaHandle.value = data.handle;
    headerMediaFileName.value = file.name;
  } catch (error) {
    mediaUploadError.value =
      error?.response?.data?.error ||
      t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.MEDIA_UPLOAD_ERROR');
    useAlert(mediaUploadError.value);
  } finally {
    isUploadingMedia.value = false;
  }
};

const buildHeaderComponent = () => {
  if (form.headerType === 'none') return null;
  if (form.headerType === 'text') {
    return form.headerText
      ? { type: 'HEADER', format: 'TEXT', text: form.headerText }
      : null;
  }
  if (!headerMediaHandle.value) return null;
  return {
    type: 'HEADER',
    format: form.headerType.toUpperCase(),
    example: { header_handle: [headerMediaHandle.value] },
  };
};

const buildButtonsComponent = () => {
  if (!form.buttons.length) return null;
  return {
    type: 'BUTTONS',
    buttons: form.buttons.map(button =>
      button.type === 'URL'
        ? { type: 'URL', text: button.text, url: button.url }
        : { type: 'QUICK_REPLY', text: button.text }
    ),
  };
};

const buildComponents = () =>
  [
    buildHeaderComponent(),
    { type: 'BODY', text: markdownToWhatsapp(form.body) },
    form.footer ? { type: 'FOOTER', text: form.footer } : null,
    buildButtonsComponent(),
  ].filter(Boolean);

// Neither the form's buttons nor the header media upload are tracked by Vuelidate
// (buttons are a dynamic list, media is an async upload) — validated here instead,
// and only surfaced in the UI once the user has tried to save (attemptedSave).
const attemptedSave = ref(false);

const isValidButtonUrl = url => /^https?:\/\//i.test((url || '').trim());

const isButtonTextInvalid = button => !button.text.trim();
const isButtonUrlInvalid = button =>
  button.type === 'URL' && !isValidButtonUrl(button.url);

// Meta allows at most 2 call-to-action (URL/phone) buttons per template, even though
// up to 10 buttons total are allowed when mixed with quick replies.
const urlButtonCount = computed(
  () => form.buttons.filter(button => button.type === 'URL').length
);
const hasTooManyUrlButtons = computed(() => urlButtonCount.value > 2);

const isButtonsInvalid = computed(
  () =>
    form.buttons.some(
      button => isButtonTextInvalid(button) || isButtonUrlInvalid(button)
    ) || hasTooManyUrlButtons.value
);

const isHeaderMediaMissing = computed(
  () => isHeaderMedia.value && !headerMediaHandle.value
);

const isFormInvalid = computed(
  () =>
    v$.value.$invalid ||
    isUploadingMedia.value ||
    isButtonsInvalid.value ||
    isHeaderMediaMissing.value
);

const validate = () => {
  v$.value.$touch();
  attemptedSave.value = true;
  return !isFormInvalid.value;
};

const resetForm = () => {
  form.name = '';
  form.category = 'MARKETING';
  form.language = 'pt_BR';
  form.headerType = 'none';
  form.headerText = '';
  form.footer = '';
  form.body = '';
  form.buttons = [];
  headerMediaHandle.value = '';
  headerMediaFileName.value = '';
  mediaUploadError.value = '';
  attemptedSave.value = false;
  bodyVariables.reset();
  headerVariables.reset();
  v$.value.$reset();
};

const getPayload = () => ({
  name: form.name.trim(),
  category: form.category,
  language: form.language,
  components: buildComponents(),
  variable_examples: bodyVariables.toExamples(),
  header_variable_examples: headerVariables.toExamples(),
});

defineExpose({
  validate,
  resetForm,
  getPayload,
  isFormInvalid,
  previewData,
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <Input
      v-model="form.name"
      :label="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.NAME')"
      :placeholder="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.NAME_PLACEHOLDER')"
      :message="
        v$.name.$error ? t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.NAME_ERROR') : ''
      "
      :message-type="v$.name.$error ? 'error' : 'info'"
      @blur="v$.name.$touch()"
    />
    <span v-if="sanitizedNamePreview" class="-mt-2 text-xs text-n-slate-10">
      {{ t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.NAME_SANITIZED_PREVIEW') }}
      <strong class="font-mono">{{ sanitizedNamePreview }}</strong>
    </span>

    <div class="grid grid-cols-2 gap-4">
      <div class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-12">{{
          t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.CATEGORY')
        }}</span>
        <SelectInput v-model="form.category" :options="CATEGORY_OPTIONS" />
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-12">{{
          t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.LANGUAGE')
        }}</span>
        <SelectInput v-model="form.language" :options="LANGUAGE_OPTIONS" />
      </div>
    </div>

    <p v-if="isAuthentication" class="text-xs text-n-slate-10">
      {{ t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.AUTHENTICATION_NOTE') }}
    </p>

    <div v-if="!isAuthentication" class="flex flex-col gap-1">
      <span class="text-sm text-n-slate-12">{{
        t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.HEADER_TYPE')
      }}</span>
      <SelectInput v-model="form.headerType" :options="HEADER_TYPE_OPTIONS" />
    </div>

    <template v-if="!isAuthentication && form.headerType === 'text'">
      <Input
        ref="headerTextInputRef"
        v-model="form.headerText"
        :label="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.HEADER_TEXT')"
        maxlength="60"
      />

      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <span class="text-sm text-n-slate-12">{{
            t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.VARIABLES')
          }}</span>
          <Button
            v-if="headerVariables.canAdd.value"
            variant="ghost"
            size="xs"
            icon="i-lucide-plus"
            :label="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.ADD_VARIABLE')"
            @click="headerVariables.add"
          />
        </div>

        <div
          v-for="(variable, index) in headerVariables.list"
          :key="index"
          class="flex items-center gap-2"
        >
          <Input
            v-model="variable.name"
            class="w-36 font-mono"
            :placeholder="
              t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.VARIABLE_NAME_PLACEHOLDER')
            "
            @focus="headerVariables.beginRename(variable)"
            @blur="headerVariables.commitRename(variable)"
          />
          <Input
            v-model="variable.example"
            class="flex-1"
            :placeholder="
              t(
                'INBOX_MGMT.MESSAGE_TEMPLATES.FORM.VARIABLE_EXAMPLE_PLACEHOLDER'
              )
            "
          />
          <Button
            variant="ghost"
            color="ruby"
            size="xs"
            icon="i-lucide-trash"
            @click="headerVariables.remove(index)"
          />
        </div>
      </div>
    </template>

    <div v-if="isHeaderMedia" class="flex flex-col gap-1">
      <span class="text-sm text-n-slate-12">{{
        t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.HEADER_MEDIA')
      }}</span>
      <span class="text-xs text-n-slate-11">{{ headerMediaHint }}</span>
      <input
        type="file"
        :accept="headerMediaAccept"
        :disabled="isUploadingMedia"
        @change="handleMediaChange"
      />
      <span v-if="isUploadingMedia" class="text-xs text-n-slate-11">
        {{ t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.MEDIA_UPLOADING') }}
      </span>
      <span v-else-if="headerMediaFileName" class="text-xs text-n-slate-11">
        {{ headerMediaFileName }}
      </span>
      <span v-if="mediaUploadError" class="text-xs text-n-ruby-9">
        {{ mediaUploadError }}
      </span>
      <span
        v-if="attemptedSave && isHeaderMediaMissing"
        class="text-xs text-n-ruby-9"
      >
        {{ t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.HEADER_MEDIA_REQUIRED') }}
      </span>
    </div>

    <div
      class="relative ltr:[&_.ProseMirror-menubar]:pl-[34px] rtl:[&_.ProseMirror-menubar]:pr-[34px]"
    >
      <Editor
        v-model="form.body"
        :label="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.BODY')"
        :placeholder="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.BODY_PLACEHOLDER')"
        :max-length="1024"
        :message="
          v$.body.$error
            ? t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.BODY_ERROR')
            : ''
        "
        :message-type="v$.body.$error ? 'error' : 'info'"
        :enable-canned-responses="false"
        @blur="v$.body.$touch()"
      />
      <div
        class="absolute top-[43px] z-20 flex items-center ltr:left-2 rtl:right-2"
      >
        <Button
          variant="ghost"
          color="slate"
          size="xs"
          @click="bodyEmoji.toggle"
        >
          <template #icon>
            <span class="i-ph-smiley-sticker size-4 flex-shrink-0" />
          </template>
        </Button>
        <OnClickOutside
          v-if="bodyEmoji.showPicker.open"
          @trigger="bodyEmoji.hide"
        >
          <EmojiInput
            :on-click="bodyEmoji.onSelect"
            class="!top-full !mt-1 ltr:!left-0 ltr:!right-auto rtl:!right-0 rtl:!left-auto"
          />
        </OnClickOutside>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <span class="text-sm text-n-slate-12">{{
          t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.VARIABLES')
        }}</span>
        <Button
          variant="ghost"
          size="xs"
          icon="i-lucide-plus"
          :label="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.ADD_VARIABLE')"
          @click="bodyVariables.add"
        />
      </div>

      <div
        v-for="(variable, index) in bodyVariables.list"
        :key="index"
        class="flex items-center gap-2"
      >
        <Input
          v-model="variable.name"
          class="w-36 font-mono"
          :placeholder="
            t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.VARIABLE_NAME_PLACEHOLDER')
          "
          @focus="bodyVariables.beginRename(variable)"
          @blur="bodyVariables.commitRename(variable)"
        />
        <Input
          v-model="variable.example"
          class="flex-1"
          :placeholder="
            t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.VARIABLE_EXAMPLE_PLACEHOLDER')
          "
        />
        <Button
          variant="ghost"
          color="ruby"
          size="xs"
          icon="i-lucide-trash"
          @click="bodyVariables.remove(index)"
        />
      </div>
    </div>

    <Input
      v-if="!isAuthentication"
      v-model="form.footer"
      :label="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.FOOTER')"
      maxlength="60"
    />

    <div v-if="!isAuthentication" class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <span class="text-sm text-n-slate-12">{{
          t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.BUTTONS')
        }}</span>
        <Button
          v-if="form.buttons.length < 3"
          variant="ghost"
          size="xs"
          icon="i-lucide-plus"
          :label="t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.ADD_BUTTON')"
          @click="addButton"
        />
      </div>
      <span
        v-if="attemptedSave && hasTooManyUrlButtons"
        class="text-xs text-n-ruby-9"
      >
        {{ t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.TOO_MANY_URL_BUTTONS') }}
      </span>

      <div
        v-for="(button, index) in form.buttons"
        :key="index"
        class="flex items-center gap-2"
      >
        <SelectInput v-model="button.type" :options="BUTTON_TYPE_OPTIONS" />
        <Input
          v-model="button.text"
          :placeholder="
            t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.BUTTON_TEXT_PLACEHOLDER')
          "
          :message="
            attemptedSave && isButtonTextInvalid(button)
              ? t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.BUTTON_TEXT_ERROR')
              : ''
          "
          :message-type="
            attemptedSave && isButtonTextInvalid(button) ? 'error' : 'info'
          "
        />
        <Input
          v-if="button.type === 'URL'"
          v-model="button.url"
          placeholder="https://"
          :message="
            attemptedSave && isButtonUrlInvalid(button)
              ? t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.BUTTON_URL_ERROR')
              : ''
          "
          :message-type="
            attemptedSave && isButtonUrlInvalid(button) ? 'error' : 'info'
          "
        />
        <Button
          variant="ghost"
          color="ruby"
          size="xs"
          icon="i-lucide-trash"
          @click="removeButton(index)"
        />
      </div>
    </div>
  </div>
</template>
